import React, { useState } from 'react';
import { X, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { Button } from './ui/Button';
interface SelfHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function SelfHelpModal({
  isOpen,
  onClose
}: SelfHelpModalProps) {
  const [pgsiAnswers, setPgsiAnswers] = useState<Record<number, number>>({});
  const [dsmAnswers, setDsmAnswers] = useState<Record<number, number>>({});
  const [showPgsiResult, setShowPgsiResult] = useState(false);
  const [showDsmResult, setShowDsmResult] = useState(false);
  if (!isOpen) return null;
  const pgsiQuestions = ['Чи робили ви ставки, які перевищували суму, яку ви дійсно могли дозволити собі програти?', 'Чи виникала у вас потреба грати на більші суми грошей, щоб отримати те саме відчуття азарту?', 'Коли ви грали, чи поверталися ви іншого дня, щоб спробувати відіграти програні гроші?', 'Чи позичали ви гроші або продавали щось, щоб отримати гроші на гру?', 'Чи відчували ви, що у вас можуть бути проблеми з азартними іграми?', "Чи спричинили азартні ігри у вас проблеми зі здоров'ям, включаючи стрес або тривогу?", 'Чи критикували люди ваші ставки або казали, що у вас є проблеми з азартними іграми, незалежно від того, чи вважали ви це правдою?', 'Чи спричинили ваші азартні ігри фінансові проблеми для вас або вашої родини?', 'Чи відчували ви провину через те, як ви граєте, або через те, що відбувається, коли ви граєте?'];
  const pgsiOptions = [{
    label: 'Ніколи',
    value: 0
  }, {
    label: 'Іноді',
    value: 1
  }, {
    label: 'Часто',
    value: 2
  }, {
    label: 'Майже завжди',
    value: 3
  }];
  const dsmQuestions = ['Чи відчуваєте ви потребу грати на все більші суми грошей, щоб досягти бажаного збудження?', 'Чи відчуваєте ви неспокій або дратівливість при спробах зменшити час гри або припинити грати?', 'Чи робили ви неодноразові безуспішні спроби контролювати, зменшити або припинити грати?', 'Чи часто ви стурбовані азартними іграми (наприклад, постійні думки про минулий ігровий досвід, планування наступної гри, роздуми про те, як дістати гроші на гру)?', 'Чи часто ви граєте, коли відчуваєте дистрес (наприклад, безпорадність, провину, тривогу, депресію)?', 'Після програшу грошей у грі, чи часто ви повертаєтеся іншого дня, щоб відігратися?', 'Чи брешете ви, щоб приховати ступінь своєї залученості в азартні ігри?', "Чи ставили ви під загрозу або втратили значущі стосунки, роботу, можливості для навчання або кар'єри через азартні ігри?", 'Чи покладаєтесь ви на інших у наданні грошей для вирішення відчайдушних фінансових ситуацій, спричинених азартними іграми?'];
  const calculatePgsiScore = () => {
    return Object.values(pgsiAnswers).reduce((sum, val) => sum + val, 0);
  };
  const getPgsiResult = (score: number) => {
    if (score === 0) return {
      text: 'Немає ризику',
      color: 'text-green-600'
    };
    if (score <= 2) return {
      text: 'Низький ризик',
      color: 'text-yellow-600'
    };
    if (score <= 7) return {
      text: 'Помірний ризик',
      color: 'text-orange-600'
    };
    return {
      text: 'Проблемна гра',
      color: 'text-red-600'
    };
  };
  const calculateDsmScore = () => {
    return Object.values(dsmAnswers).reduce((sum, val) => sum + val, 0);
  };
  const getDsmResult = (score: number) => {
    if (score <= 3) return {
      text: 'Немає розладу',
      color: 'text-green-600'
    };
    if (score <= 5) return {
      text: 'Легкий ступінь',
      color: 'text-yellow-600'
    };
    if (score <= 7) return {
      text: 'Помірний ступінь',
      color: 'text-orange-600'
    };
    return {
      text: 'Важкий ступінь',
      color: 'text-red-600'
    };
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-506">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-507" />
      <div className="relative bg-[#F5F0E8] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" data-id="element-508">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200" data-id="element-509">
          <h2 className="text-2xl font-serif font-bold text-[#0A5E5C]" data-id="element-510">
            Самодопомога
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500" data-id="element-511">
            <X size={24} data-id="element-512" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-12 text-gray-800" data-id="element-513">
          {/* Intro */}
          <section className="space-y-4" data-id="element-514">
            <p className="text-lg leading-relaxed" data-id="element-515">
              Цей розділ створено для тих, хто хоче краще зрозуміти власну
              ігрову поведінку, для тих, хто намагається зрозуміти, що таке
              азартні ігри і чому вони можуть нести ризики, а також для тих, хто
              хоче оцінити можливі ризики від азартних ігор та отримати
              практичні інструменти самоконтролю.
            </p>
            <p className="leading-relaxed" data-id="element-516">
              Азартні ігри можуть бути формою розваги та приносити задоволення.
              Однак іноді, коли людина з певних причин втрачає контроль, азартні
              ігри можуть почати негативно впливати на її фінанси, емоційний
              стан, стосунки з близькими та якість життя.
            </p>
            <p className="leading-relaxed" data-id="element-517">
              Тому дуже важливо бути уважним до себе, до власних емоцій,
              почуттів, реакцій та станів, які супроводжують вас до, під час та
              після гри. Дуже важливо бути в контакті з собою і усвідомлювати
              все, що відбувається з вами, коли ви граєте, коли думаєте про гру.
              Можливо, ви використовуєте гру, щоб втекти від нестерпної
              реальності, яка вам з якихось причин не подобається. Можливо, ви
              відчуваєте неймовірне емоційне збудження під час гри, яке більше
              ніде вам недоступне в житті. Можливо, ви вирішили, що азартні ігри
              - це легкий спосіб заробити гроші. Або, можливо, всі ці думки та
              почуття присутні у вас одночасно. Не відвертайтеся від них і не
              засуджуйте себе, проявіть трохи уваги до себе, і якщо ви читаєте
              цей текст, то ви точно здатні і готові повернути контроль над
              своєю грою і своїм життям. Вірте в себе. Тільки ви насправді
              вирішуєте, хто і що контролює вас і ваше життя, і навіть якщо
              через певні обставини ви віддали контроль над собою грі або чомусь
              іншому, що вас захопило, ви точно можете повернути цей контроль.
              Це ваше абсолютне право, право контролювати себе і своє життя, і
              навіть якщо зараз це здається вам неможливим, просто хоча б
              допустіть таку думку і спробуйте рухатися вперед, і простір життя
              почне надавати вам підтримку і допомогу на шляху повернення до
              себе. Ми віримо у вас і вашу силу.
            </p>
          </section>

          {/* Tests Intro */}
          <section className="space-y-6" data-id="element-518">
            <h3 className="text-2xl font-serif font-bold text-[#0A5E5C]" data-id="element-519">
              Тести для самооцінки ризику ігрової залежності
            </h3>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg flex gap-3" data-id="element-520">
              <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-1" data-id="element-521" />
              <div data-id="element-522">
                <h4 className="font-bold text-yellow-800" data-id="element-523">Важливо</h4>
                <p className="text-yellow-700 text-sm mt-1" data-id="element-524">
                  Наведені нижче тести є лише інструментами для самодіагностики.
                  Вони не є медичним діагнозом і не можуть замінити консультацію
                  психолога, психотерапевта чи іншого кваліфікованого
                  спеціаліста. Якщо результати тесту викликають занепокоєння або
                  ви відчуваєте, що азартні ігри починають впливати на ваше
                  життя, рекомендується звернутися за професійною допомогою.
                </p>
              </div>
            </div>
          </section>

          {/* PGSI Test */}
          <section className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100" data-id="element-525">
            <h3 className="text-xl font-serif font-bold text-[#0A5E5C] mb-2" data-id="element-526">
              Індекс тяжкості проблемної гри (PGSI)
            </h3>
            <p className="text-gray-600 text-sm mb-6" data-id="element-527">
              PGSI - це міжнародно визнаний інструмент для оцінки ризику
              проблемної ігрової поведінки.
              <br data-id="element-528" />
              Розроблено: Julie Ferris та Harold Wynne (2001)
              <br data-id="element-529" />
              В рамках: Canadian Problem Gambling Index (CPGI)
              <br data-id="element-530" />
              На замовлення: Canadian Centre on Substance Abuse
            </p>
            <div className="bg-blue-50 p-4 rounded-lg mb-8 flex gap-3" data-id="element-531">
              <Info className="text-blue-600 flex-shrink-0" data-id="element-532" />
              <p className="text-blue-800 text-sm" data-id="element-533">
                <strong data-id="element-534">Інструкція:</strong> Дайте відповідь на кожне запитання,
                оцінюючи свою поведінку за останні 12 місяців.
              </p>
            </div>

            <div className="space-y-8" data-id="element-535">
              {pgsiQuestions.map((q, idx) => <div key={idx} className="space-y-3" data-id="element-536">
                  <p className="font-medium text-gray-800" data-id="element-537">
                    {idx + 1}. {q}
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-538">
                    {pgsiOptions.map(opt => <button key={opt.value} onClick={() => setPgsiAnswers(prev => ({
                  ...prev,
                  [idx]: opt.value
                }))} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${pgsiAnswers[idx] === opt.value ? 'bg-[#0A5E5C] text-white border-[#0A5E5C]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#0A5E5C] hover:text-[#0A5E5C]'}`} data-id="element-539">
                        {opt.label}
                      </button>)}
                  </div>
                </div>)}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100" data-id="element-540">
              {!showPgsiResult ? <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-541">
                  <span className="text-sm text-gray-500" data-id="element-542">
                    Дано відповідей: {Object.keys(pgsiAnswers).length} з 9
                  </span>
                  <Button onClick={() => setShowPgsiResult(true)} disabled={Object.keys(pgsiAnswers).length < 9} data-id="element-543">
                    Переглянути результати
                  </Button>
                </div> : <div className="bg-gray-50 p-6 rounded-xl text-center space-y-2" data-id="element-544">
                  <p className="text-gray-600" data-id="element-545">Ваш результат:</p>
                  <p className="text-4xl font-bold" data-id="element-546">{calculatePgsiScore()}</p>
                  <p className={`text-xl font-bold ${getPgsiResult(calculatePgsiScore()).color}`} data-id="element-547">
                    {getPgsiResult(calculatePgsiScore()).text}
                  </p>
                  <button onClick={() => {
                setPgsiAnswers({});
                setShowPgsiResult(false);
              }} className="text-sm text-[#0A5E5C] hover:underline mt-4 inline-block" data-id="element-548">
                    Пройти тест знову
                  </button>
                </div>}
            </div>
          </section>

          {/* DSM-5 Test */}
          <section className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100" data-id="element-549">
            <h3 className="text-xl font-serif font-bold text-[#0A5E5C] mb-2" data-id="element-550">
              Критерії ігрового розладу DSM-5
            </h3>
            <p className="text-gray-600 text-sm mb-6" data-id="element-551">
              Ці критерії використовуються для клінічної оцінки ігрового
              розладу. У цьому виданні ігрова залежність була офіційно
              класифікована в категорію поведінкових залежностей.
              <br data-id="element-552" />
              Розробник: American Psychiatric Association (2013)
              <br data-id="element-553" />
              Джерело: Diagnostic and Statistical Manual of Mental Disorders,
              Fifth Edition (DSM-5)
            </p>
            <div className="bg-blue-50 p-4 rounded-lg mb-8 flex gap-3" data-id="element-554">
              <Info className="text-blue-600 flex-shrink-0" data-id="element-555" />
              <p className="text-blue-800 text-sm" data-id="element-556">
                <strong data-id="element-557">Інструкція:</strong> Дайте відповідь Так або Ні,
                оцінюючи свій стан за останні 12 місяців.
              </p>
            </div>

            <div className="space-y-8" data-id="element-558">
              {dsmQuestions.map((q, idx) => <div key={idx} className="space-y-3" data-id="element-559">
                  <p className="font-medium text-gray-800" data-id="element-560">
                    {idx + 1}. {q}
                  </p>
                  <div className="flex gap-2" data-id="element-561">
                    <button onClick={() => setDsmAnswers(prev => ({
                  ...prev,
                  [idx]: 1
                }))} className={`px-6 py-2 rounded-full text-sm font-medium transition-colors border ${dsmAnswers[idx] === 1 ? 'bg-[#0A5E5C] text-white border-[#0A5E5C]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#0A5E5C] hover:text-[#0A5E5C]'}`} data-id="element-562">
                      Так
                    </button>
                    <button onClick={() => setDsmAnswers(prev => ({
                  ...prev,
                  [idx]: 0
                }))} className={`px-6 py-2 rounded-full text-sm font-medium transition-colors border ${dsmAnswers[idx] === 0 ? 'bg-[#0A5E5C] text-white border-[#0A5E5C]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#0A5E5C] hover:text-[#0A5E5C]'}`} data-id="element-563">
                      Ні
                    </button>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100" data-id="element-564">
              {!showDsmResult ? <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-565">
                  <span className="text-sm text-gray-500" data-id="element-566">
                    Дано відповідей: {Object.keys(dsmAnswers).length} з 9
                  </span>
                  <Button onClick={() => setShowDsmResult(true)} disabled={Object.keys(dsmAnswers).length < 9} data-id="element-567">
                    Переглянути результати
                  </Button>
                </div> : <div className="bg-gray-50 p-6 rounded-xl text-center space-y-2" data-id="element-568">
                  <p className="text-gray-600" data-id="element-569">
                    Ваш результат (позитивних відповідей):
                  </p>
                  <p className="text-4xl font-bold" data-id="element-570">{calculateDsmScore()}</p>
                  <p className={`text-xl font-bold ${getDsmResult(calculateDsmScore()).color}`} data-id="element-571">
                    {getDsmResult(calculateDsmScore()).text}
                  </p>
                  <button onClick={() => {
                setDsmAnswers({});
                setShowDsmResult(false);
              }} className="text-sm text-[#0A5E5C] hover:underline mt-4 inline-block" data-id="element-572">
                    Пройти тест знову
                  </button>
                </div>}
            </div>
          </section>

          {/* Signs */}
          <section className="space-y-6" data-id="element-573">
            <h3 className="text-2xl font-serif font-bold text-[#0A5E5C]" data-id="element-574">
              Які ознаки проблемної гри?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-575">
              <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-576">
                <h4 className="font-bold text-[#D4A843] mb-4" data-id="element-577">
                  Поведінкові ознаки
                </h4>
                <ul className="space-y-2 text-sm" data-id="element-578">
                  <li className="flex gap-2" data-id="element-579">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-580" />{' '}
                    постійні думки про азартні ігри
                  </li>
                  <li className="flex gap-2" data-id="element-581">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-582" />{' '}
                    бажання збільшувати ставки
                  </li>
                  <li className="flex gap-2" data-id="element-583">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-584" />{' '}
                    спроби відіграти програні гроші
                  </li>
                  <li className="flex gap-2" data-id="element-585">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-586" />{' '}
                    труднощі із зупинкою гри
                  </li>
                  <li className="flex gap-2" data-id="element-587">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-588" />{' '}
                    приховування ігрової активності
                  </li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-589">
                <h4 className="font-bold text-[#D4A843] mb-4" data-id="element-590">
                  Емоційні ознаки
                </h4>
                <ul className="space-y-2 text-sm" data-id="element-591">
                  <li className="flex gap-2" data-id="element-592">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-593" />{' '}
                    відчуття провини після гри
                  </li>
                  <li className="flex gap-2" data-id="element-594">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-595" />{' '}
                    дратівливість або тривога при неможливості грати
                  </li>
                  <li className="flex gap-2" data-id="element-596">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-597" />{' '}
                    використання гри для втечі від стресу або проблем
                  </li>
                  <li className="flex gap-2" data-id="element-598">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-599" />{' '}
                    відчуття втрати контролю
                  </li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-600">
                <h4 className="font-bold text-[#D4A843] mb-4" data-id="element-601">
                  Фінансові ознаки
                </h4>
                <ul className="space-y-2 text-sm" data-id="element-602">
                  <li className="flex gap-2" data-id="element-603">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-604" />{' '}
                    витрати понад власні можливості
                  </li>
                  <li className="flex gap-2" data-id="element-605">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-606" />{' '}
                    позики або кредити для гри
                  </li>
                  <li className="flex gap-2" data-id="element-607">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-608" />{' '}
                    борги або фінансові труднощі
                  </li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-609">
                <h4 className="font-bold text-[#D4A843] mb-4" data-id="element-610">
                  Соціальні ознаки
                </h4>
                <ul className="space-y-2 text-sm" data-id="element-611">
                  <li className="flex gap-2" data-id="element-612">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-613" />{' '}
                    конфлікти з родиною
                  </li>
                  <li className="flex gap-2" data-id="element-614">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-615" />{' '}
                    проблеми на роботі
                  </li>
                  <li className="flex gap-2" data-id="element-616">
                    <CheckCircle2 size={16} className="text-[#0A5E5C] flex-shrink-0 mt-0.5" data-id="element-617" />{' '}
                    соціальна ізоляція
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Where to start */}
          <section className="space-y-4" data-id="element-618">
            <h3 className="text-2xl font-serif font-bold text-[#0A5E5C]" data-id="element-619">
              З чого почати?
            </h3>
            <p className="leading-relaxed" data-id="element-620">
              Якщо азартні ігри дійсно впливають на ваше життя, і ви готові це
              визнати, або принаймні допустити таку думку, це вже успішний
              старт. Зрештою, визнання та усвідомлення вимагають великої сили, і
              якщо у вас є ця сила визнати поточний стан речей у вашому житті, у
              вас точно є сила діяти, щоб допомогти собі. Дуже важливо дати
              цьому усвідомленню простір і не намагатися боротися з ним або з
              собою. Спробуйте з цікавістю дослідити, коли і як ви могли
              втратити контроль над собою і віддати його грі, за яких обставин
              це сталося, що саме ви відчували і що могло бути причиною такого
              рішення, чи було воно свідомим, або ви не пам'ятаєте, як раптом
              зрозуміли, що не можете зупинитися і не знайшли в собі сил відразу
              це визнати.
            </p>
            <p className="leading-relaxed" data-id="element-621">
              Якщо у вас є хтось дуже близький і безпечний для вас, хтось, хто
              приймає вас і не засуджує, ви могли б поділитися всіма цими
              думками, спогадами та почуттями, які виникають у вас під час
              такого дослідження. Добрий, приймаючий погляд близької, безпечної
              людини може дуже допомогти. Подумайте про це, можливо, поруч є
              людина, якій ви могли б відкритися і не бути самотнім у цьому
              дослідженні себе.
            </p>
          </section>

          {/* Help yourself */}
          <section className="space-y-6" data-id="element-622">
            <h3 className="text-2xl font-serif font-bold text-[#0A5E5C]" data-id="element-623">
              Що ви можете зробити, щоб допомогти собі?
            </h3>
            <p className="leading-relaxed" data-id="element-624">
              Якщо ви вирішили взяти під контроль свою гру і вважаєте, що вам
              достатньо просто підвищити свою обізнаність про ігровий процес, то
              наступні прості, але ефективні поради можуть вам допомогти.
            </p>

            <div className="space-y-4" data-id="element-625">
              {[{
              title: 'Встановіть для себе ліміти',
              desc: 'Визначте суму, яку ви можете дозволити собі витратити на розваги, і не перевищуйте її.'
            }, {
              title: 'Контролюйте час гри',
              desc: "Встановіть часові обмеження або використовуйте таймер. Пам'ятайте: на легальних сайтах вам не дозволять грати без самостійного встановлення таких лімітів. Будьте свідомими при встановленні цих лімітів."
            }, {
              title: 'Не намагайтеся відігратися',
              desc: 'Спроби повернути програні гроші часто призводять до ще більших втрат.'
            }, {
              title: 'Уникайте гри під емоційним впливом',
              desc: 'Стрес, алкоголь або сильні емоції можуть призвести до імпульсивних рішень.'
            }, {
              title: 'Робіть регулярні перерви',
              desc: 'Регулярні паузи допомагають зберігати контроль. Під час таких пауз спробуйте проаналізувати свою поведінку під час гри, свій загальний емоційний стан та емоційний стан безпосередньо під час гри. Запитайте себе, чи дійсно азартні ігри є для вас лише розвагою, що приносить позитивні емоції без будь-якого негативного впливу на ваш фінансовий стан, емоційний стан, ваші думки, настрій, ваші стосунки з навколишнім світом та людьми навколо вас. Намагайтеся робити такий аналіз регулярно.'
            }].map((item, i) => <div key={i} className="flex gap-4" data-id="element-626">
                  <div className="w-8 h-8 rounded-full bg-[#D4A843] text-[#0A5E5C] font-bold flex items-center justify-center flex-shrink-0" data-id="element-627">
                    {i + 1}
                  </div>
                  <div data-id="element-628">
                    <h4 className="font-bold text-gray-800" data-id="element-629">{item.title}</h4>
                    <p className="text-gray-600 text-sm mt-1" data-id="element-630">{item.desc}</p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 p-6 bg-white rounded-xl shadow-sm border border-gray-100 space-y-6" data-id="element-631">
              <p className="font-medium text-[#0A5E5C]" data-id="element-632">
                Якщо ви зрозуміли, що азартні ігри негативно впливають на ваше
                життя, настрій, фінанси, стосунки, думки, і вирішили діяти, щоб
                це змінити, наступні кроки можуть бути корисними для вас.
              </p>

              <div className="space-y-6" data-id="element-633">
                <div data-id="element-634">
                  <h4 className="font-bold text-gray-800 flex items-center gap-2 mb-2" data-id="element-635">
                    <span className="w-6 h-6 rounded-full bg-[#0A5E5C] text-white text-sm flex items-center justify-center" data-id="element-636">
                      1
                    </span>
                    Самообмеження
                  </h4>
                  <p className="text-sm text-gray-600 pl-8" data-id="element-637">
                    Подайте заяву про самообмеження через веб-сайт Державного
                    агентства "КРАІЛ" (www.gc.gov.ua) або www.playcity.gov.ua в
                    розділі "Для громадян" є опція "Заява про обмеження", або ви
                    можете подати таку заяву на сайті будь-якого легального
                    організатора азартних ігор. В результаті подання такої заяви
                    ваші дані будуть внесені до Реєстру осіб, яким обмежено
                    доступ до гральних закладів та/або участь в азартних іграх.
                    Але ви повинні усвідомлювати, що внесення ваших даних до
                    Реєстру обмежить ваш доступ до азартних ігор лише у
                    легальних операторів. Тому ви можете використовувати
                    додаткові інструменти.
                  </p>
                </div>
                <div data-id="element-638">
                  <h4 className="font-bold text-gray-800 flex items-center gap-2 mb-2" data-id="element-639">
                    <span className="w-6 h-6 rounded-full bg-[#0A5E5C] text-white text-sm flex items-center justify-center" data-id="element-640">
                      2
                    </span>
                    BETBLOCKER
                  </h4>
                  <p className="text-sm text-gray-600 pl-8" data-id="element-641">
                    Використовуйте безкоштовний інструмент блокування
                    BETBLOCKER, про який ви можете дізнатися на сайті{' '}
                    <a href="https://betblocker.org" target="_blank" rel="noreferrer" className="text-[#D4A843] hover:underline" data-id="element-642">
                      betblocker.org
                    </a>{' '}
                    або завантажити його як мобільний додаток. Цей інструмент
                    можна встановити для різних типів пристроїв і операційних
                    систем, і він працює з території України, вся інформація на
                    сайті і в додатку є українською мовою.
                  </p>
                </div>
                <div data-id="element-643">
                  <h4 className="font-bold text-gray-800 flex items-center gap-2 mb-2" data-id="element-644">
                    <span className="w-6 h-6 rounded-full bg-[#0A5E5C] text-white text-sm flex items-center justify-center" data-id="element-645">
                      3
                    </span>
                    Gamban
                  </h4>
                  <p className="text-sm text-gray-600 pl-8" data-id="element-646">
                    Використовуйте інший інструмент блокування Gamban{' '}
                    <a href="https://gamban.com" target="_blank" rel="noreferrer" className="text-[#D4A843] hover:underline" data-id="element-647">
                      gamban.com
                    </a>
                    . Цей інструмент працює в Україні, але не має україномовної
                    версії і є платним (є безкоштовна пробна версія).
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Help a loved one */}
          <section className="space-y-6" data-id="element-648">
            <h3 className="text-2xl font-serif font-bold text-[#0A5E5C]" data-id="element-649">
              Що робити, якщо хтось із ваших близьких неконтрольовано грає
            </h3>
            <p className="leading-relaxed" data-id="element-650">
              Коли близька людина стикається з проблемною ігровою поведінкою, це
              може викликати тривогу, гнів, розгубленість або безсилля. Важливо
              усвідомити свої емоції та реакції, спробувати опанувати себе і
              повірити, що ваша підтримка та увага дійсно можуть допомогти, якщо
              ви самі будете свідомими та спокійними. Також важливо діяти
              обережно. Спробуйте почати з простої спокійної розмови, щоб
              людина, яка, можливо, ще не визнає реальний стан речей, відчула,
              що ви готові їй допомогти без засудження чи звинувачень. Ваш щирий
              інтерес і увага до близької людини, її емоційного стану та
              переживань у житті, ваша підтримка та любов можуть стати точкою
              опори, де ваша близька людина зможе прийняти рішення визнати
              проблему та обрати діяти з вашою підтримкою. Водночас не нехтуйте
              увагою до себе та власного емоційного стану. Подбайте про себе та
              свої ресурси.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-651">
              {[{
              title: 'Спробуйте зрозуміти проблему',
              desc: "Ігрова залежність - це складна поведінкова проблема, яка може бути пов'язана зі стресом, емоційними труднощами або бажанням уникнути проблем. Спробуйте зрозуміти свою близьку людину, чого вона уникає, який стрес міг стати причиною занурення в неконтрольовану гру."
            }, {
              title: 'Говоріть спокійно і без звинувачень',
              desc: 'Намагайтеся будувати розмови так, щоб створити атмосферу довіри та прийняття ситуації. За жодних обставин не слід засуджувати, звинувачувати або жаліти. Досліджуйте проблему разом і не засуджуйте нічого, що виявите.'
            }, {
              title: 'Не намагайтеся контролювати все',
              desc: 'Надмірний контроль може призвести до конфліктів або приховування проблеми.'
            }, {
              title: 'Не покривайте борги',
              desc: 'Оплата боргів або постійна фінансова допомога можуть лише відстрочити вирішення проблеми.'
            }, {
              title: 'Заохочуйте звернення по допомогу',
              desc: 'Ви можете запропонувати звернутися до психолога, консультанта або груп підтримки. Головне - не тиснути.'
            }, {
              title: 'Підтримуйте позитивні зміни',
              desc: 'Навіть маленькі кроки - наприклад, скорочення часу гри - можуть бути важливими. Складіть план маленьких кроків разом.'
            }, {
              title: 'Допоможіть знайти альтернативні заняття',
              desc: 'Спорт, хобі, подорожі або навчання можуть допомогти замінити час і увагу, які раніше займала гра.'
            }, {
              title: 'Встановіть власні кордони',
              desc: 'Важливо визначити, що для вас є неприйнятним. Не давайте гроші на гру і не беріть на себе фінансову відповідальність за чужі борги.'
            }, {
              title: 'Подбайте про себе',
              desc: 'Жити поруч з людиною із залежністю може бути емоційно важко. Ви не зможете повноцінно допомогти комусь, якщо самі втратили емоційну рівновагу.'
            }, {
              title: "Пам'ятайте, що зміни потребують часу",
              desc: 'Подолання проблемної поведінки може зайняти час, але підтримка близьких допомагає рухатися вперед.'
            }].map((item, i) => <div key={i} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100" data-id="element-652">
                  <h4 className="font-bold text-[#0A5E5C] mb-2 flex items-center gap-2" data-id="element-653">
                    <span className="text-[#D4A843]" data-id="element-654">{i + 1}.</span>{' '}
                    {item.title}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-655">{item.desc}</p>
                </div>)}
            </div>
          </section>

          {/* Financial security */}
          <section className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100 space-y-4" data-id="element-656">
            <h3 className="text-xl font-serif font-bold text-[#0A5E5C]" data-id="element-657">
              Якщо існує загроза фінансовій безпеці вашої родини через
              неконтрольовану гру вашої близької людини (родича)
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed" data-id="element-658">
              Якщо ваша близька людина (родич) не визнає наявність проблем з
              азартними іграми через певні обставини і не виявляє бажання шукати
              вихід для її подолання, і, можливо, неконтрольована гра загрожує
              фінансовій безпеці вашої родини, ви можете скористатися деякими
              правовими інструментами для захисту.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed" data-id="element-659">
              Подайте Заяву про обмеження такої особи до Державного агентства
              (КРАІЛ) через веб-сайт www.gc.gov.ua або www.playcity.gov.ua в
              розділі "Для громадян" є опція "Заява про обмеження", або в
              письмовій формі. В результаті подання такої заяви особа буде
              внесена до Реєстру осіб, яким обмежено доступ до гральних закладів
              та/або участь в азартних іграх.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed" data-id="element-660">
              Важливо, що ви можете подати таку заяву лише щодо родича першого
              ступеня споріднення, якими можуть бути батьки, чоловік, дружина,
              діти (в тому числі усиновлені) або, якщо ви є законним
              представником особи, яку необхідно обмежити в доступі до азартних
              ігор. Разом із заявою ви повинні подати документ, що підтверджує
              ваші родинні зв'язки.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed" data-id="element-661">
              Заява може бути подана лише щодо особи, якій виповнилося 21 рік.
              Особи віком до 21 року не можуть бути допущені до азартних ігор
              відповідно до вимог чинного законодавства.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg" data-id="element-662">
              <p className="text-sm font-medium mb-2" data-id="element-663">
                До заяви необхідно додати обґрунтування та документи, що
                підтверджують необхідність встановлення обмеження через одну з
                наступних обставин:
              </p>
              <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1" data-id="element-664">
                <li data-id="element-665">
                  перевищення витрат на гру над особистими доходами особи, що
                  ставить таку особу або її сім'ю у скрутне матеріальне
                  становище;
                </li>
                <li data-id="element-666">
                  перевищення витрат на гру над особистими доходами особи, що
                  здійснюється з використанням позикових коштів;
                </li>
                <li data-id="element-667">
                  наявність боргових зобов'язань на суму понад 100 прожиткових
                  мінімумів доходів громадян;
                </li>
                <li data-id="element-668">
                  ухилення особи від сплати аліментів більше трьох місяців;
                </li>
                <li data-id="element-669">
                  якщо особа або члени її сім'ї є отримувачами житлової субсидії
                  або пільг на оплату житлово-комунальних послуг.
                </li>
              </ul>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed" data-id="element-670">
              Подавши таку заяву, ви можете обмежити доступ зазначеної особи до
              азартних ігор на строк до 6 місяців. Для обмеження доступу на
              довший термін необхідно звернутися до суду.
            </p>
          </section>
        </div>
      </div>
    </div>;
}